import { Injectable, inject } from '@angular/core';
import { HelperFunctions } from '@curbnturf/entities';
import {
  IActivityLocation,
  IAmenityList,
  IPhoto,
  ISearchableBoondocking,
  ISearchableListing,
  SearchQuery,
} from '@curbnturf/objects';
import { map, Observable } from 'rxjs';
import {
  Amenity,
  Category,
  IBoondockingListing,
  IPrivateListing,
  ListingAgency,
  sortAmenities,
} from './mock-listings.data';
import { IPoi, PoiCost } from '../pois/poi.types';
import { LiveSearchApiService } from '../backend-api/live-search-api.service';
import { reverseCancellationPolicy } from './draft-listing-real-mapper';

const AGENCY_VALUES: ListingAgency[] = ['BLM', 'USFS', 'NPS', 'State Park', 'Army Corps', 'County', 'Other'];

function amenitiesToList(amenities: IAmenityList | undefined): Amenity[] {
  if (!amenities) return [];
  return sortAmenities(
    (Object.keys(amenities) as Amenity[]).filter((key) => (amenities as Record<string, boolean>)[key]),
  );
}

function photoToUrl(photo: IPhoto): string {
  return HelperFunctions.getPhotoUrl(photo);
}

function firstPhotoUrl(photos: IPhoto[] | undefined): string {
  if (photos && photos.length > 0) {
    return HelperFunctions.getPhotoUrlSquareMedium(photos[0]);
  }
  return HelperFunctions.getPhotoUrlSquareMedium({ key: '' } as IPhoto);
}

/** All of a listing's real uploaded photos, when there are any — feeds `IListingBase.images`
 *  so `getListingDetail()` can show the listing's own photos instead of stock fill-ins. */
function allPhotoUrls(photos: IPhoto[] | undefined): string[] | undefined {
  return photos && photos.length > 0 ? photos.map((p) => HelperFunctions.getPhotoUrlSquareMedium(p)) : undefined;
}

function toAgency(agency: string | undefined): ListingAgency {
  return (AGENCY_VALUES as string[]).includes(agency || '') ? (agency as ListingAgency) : 'Other';
}

/** ISearchableListing/ISearchableBoondocking don't carry a precomputed display string —
 *  mirrors the same "City, ST" formula the search servers use internally. */
function toLocation(city: string | undefined, state: string | undefined): string {
  if (city && state) return `${city}, ${state}`;
  return state || '';
}

/**
 * Adapts `LiveSearchApiService` (a local, HttpClient-only fork of
 * `@curbnturf/listing-search`'s SearchService — see that file's header comment) onto
 * cnt-landing's own IPrivateListing/IBoondockingListing/IPoi shapes, replacing the
 * ALL_LISTINGS/MOCK_BOONDOCKING/MOCK_POIS mock data in cnt-workspace-search-results /
 * cnt-search-map. See "Wire cnt-landing search results/map to live search servers" plan, Part C.
 */
@Injectable({ providedIn: 'root' })
export class LiveSearchService {
  private searchApi = inject(LiveSearchApiService);

  /** Private listings only, without the boondocking and POI requests findAll makes. */
  findPrivateListings(query: SearchQuery = new SearchQuery({})): Observable<IPrivateListing[]> {
    return this.searchApi.findListings(query).pipe(map((listings) => listings.map((l) => this.toPrivateListing(l))));
  }

  /** Fetches private listings, boondocking sites, and POI utility locations in one call.
   *  Defaults to no filters (mirrors the existing bulk-fetch-then-filter-client-side pattern
   *  used elsewhere, e.g. BoondockService.preload()) — pass a scoped `query` for a filtered
   *  fetch instead. `listingsQuery` overrides `query` for the private listings only. */
  findAll(
    query: SearchQuery = new SearchQuery({}),
    listingsQuery: SearchQuery = query,
  ): Observable<{ listings: IPrivateListing[]; boondocking: IBoondockingListing[]; pois: IPoi[] }> {
    return this.searchApi.find(query, listingsQuery).pipe(
      map(({ listings, boondockListings, poiListings }) => ({
        listings: listings.map((l) => this.toPrivateListing(l)),
        boondocking: boondockListings.map((b) => this.toBoondockingListing(b)),
        pois: poiListings.map((p) => this.toPoi(p)),
      })),
    );
  }

  /** Date-filtered private-listing search — every listing returned is already
   *  availability-filtered server-side for the requested date range, so
   *  callers don't need a separate per-listing availability check afterward.
   *  Requires `query` to carry both a date range and a map bbox. */
  findAvailable(query: SearchQuery): Observable<{ listings: IPrivateListing[] }> {
    return this.searchApi
      .findAvailable(query)
      .pipe(map(({ listings }) => ({ listings: listings.map((l) => this.toPrivateListing(l)) })));
  }

  private toPrivateListing(listing: ISearchableListing): IPrivateListing {
    return {
      id: listing.id,
      title: listing.name || '',
      location: toLocation(listing.city, listing.state),
      coordinates: { lat: listing.coordinates.lat, lon: listing.coordinates.lon },
      category: (listing.category || 'attraction') as Category,
      landTypes: [],
      amenities: amenitiesToList(listing.amenities),
      image: firstPhotoUrl(listing.photos),
      images: allPhotoUrls(listing.photos),
      price: listing.price || 0,
      rating: listing.rating || 0,
      reviewCount: listing.ratingCount || 0,
      reservationProcess: listing.reservationProcess === 'instant' ? 'instant' : 'standard',
      extrasCount: listing.extrasCount ?? 0,
      // Undefined when the backend sends none: unknown means no chip, not 'moderate'.
      cancellationTier: reverseCancellationPolicy(listing.cancellationPolicy),
      // Left undefined when the backend has no figure, so the card shows no fit
      // verdict rather than one computed from `getListingDetail()`'s flat 35.
      maxRigLength: listing.maxLength,
    };
  }

  private toBoondockingListing(boondock: ISearchableBoondocking): IBoondockingListing {
    return {
      id: boondock.id,
      title: boondock.name || '',
      location: toLocation(boondock.city, boondock.state),
      state: boondock.state || undefined,
      coordinates: { lat: boondock.coordinates.lat, lon: boondock.coordinates.lon },
      category: 'nature',
      landTypes: [],
      amenities: amenitiesToList(boondock.amenities),
      image: firstPhotoUrl(boondock.photos),
      images: allPhotoUrls(boondock.photos),
      agency: toAgency(boondock.agency),
    };
  }

  private toPoi(poi: IActivityLocation): IPoi {
    const address = poi.address
      ? [poi.address.address, poi.address.city, poi.address.state].filter((s) => !!s).join(', ')
      : '';

    return {
      id: String(poi.id ?? ''),
      categoryDump: poi.categoryDump,
      categoryRestArea: poi.categoryRestArea,
      categoryPropane: poi.categoryPropane,
      categoryPotableWater: poi.categoryPotableWater,
      name: poi.name || '',
      coordinates: poi.coordinates ? { lat: poi.coordinates.lat, lon: poi.coordinates.lon } : { lat: 0, lon: 0 },
      address,
      photos: (poi.photos || []).map((p) => photoToUrl(p)),
      cost: (poi.cost || 'unknown') as PoiCost,
      priceNote: poi.priceNote,
      lastVerified: poi.lastVerified || '',
      amenities: [],
      maxRigLength: poi.maxRigLength,
      cellSignal: this.toCellSignal(poi.cellSignalStrength),
      hours: poi.hours,
    };
  }

  /** IActivityLocation.cellSignalStrength is 0 (none) - 4 (excellent); IPoi.cellSignal is
   *  1-5 bars with no "none" value — 0 maps to undefined (no reading), 1-4 pass through. */
  private toCellSignal(strength: number | undefined): 1 | 2 | 3 | 4 | 5 | undefined {
    if (strength === undefined || strength <= 0) return undefined;
    return strength as 1 | 2 | 3 | 4;
  }
}
